import { cn } from "@win-reality/react-ui";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { ReactNode } from "react";

import { AutoBadge } from "../ResultControls";

type TagSectionProps = {
  label: string;
  /** Section name, set as `data-section` */
  section?: string;
  /** Receives keyboard shortcuts */
  isActive?: boolean;
  children: ReactNode;
  /** Test hook: literal id from the call site */
  "data-testid"?: string;
};

/** Labeled section that always shows its content (Results From This Play). */
export const TagSection = ({
  label,
  section,
  isActive = false,
  isDisabled = false,
  children,
  "data-testid": testId,
}: TagSectionProps & {
  /** Grayed out and not interactive, e.g. while waiting on the pitch */
  isDisabled?: boolean;
}) => (
  <section
    data-testid={testId}
    data-section={section}
    aria-current={isActive ? "step" : undefined}
    // Takes its controls out of focus and clicks without disabling each one
    inert={isDisabled}
    // No side padding: its rows line up with the tag cards above
    className={cn("space-y-3 py-3", isDisabled && "opacity-50")}
  >
    <h4 className="text-sm font-medium uppercase tracking-wide text-tagging-muted">
      {label}
    </h4>
    {children}
  </section>
);

type CollapsibleTagSectionProps = TagSectionProps & {
  section: string;
  /** Kebab-case section name for the badge's `data-field` */
  field: string;
  /** Picked value's label, shown in the summary row; null shows "-" */
  value: string | null;
  /** The value's API enum string, for tests */
  rawValue: string | null;
  isAutoDetected: boolean;
  /** Its value is on the way (pitch type before the pitch arrives) */
  isLoading?: boolean;
  /** The pitch result rules the section out (e.g. contact type on a ball) */
  isDisabled: boolean;
  onToggle: () => void;
};

/**
 * Tag section that collapses to a summary row with its value. Only the
 * active one is open; the summary row opens or closes it.
 */
export const CollapsibleTagSection = ({
  label,
  section,
  field,
  isActive = false,
  value,
  rawValue,
  isAutoDetected,
  isLoading = false,
  isDisabled,
  onToggle,
  children,
  "data-testid": testId,
}: CollapsibleTagSectionProps) => {
  const isOpen = isActive && !isDisabled;
  const Chevron = isOpen ? ChevronUp : ChevronDown;

  return (
    <section
      data-testid={testId}
      data-section={section}
      aria-current={isOpen ? "step" : undefined}
      className={cn(
        // Border
        "rounded-xl",
        // Background
        "bg-tagging-sidebar-bg",
        // Effects
        "shadow-[0_0_4px_0_rgba(0,0,0,0.25)] transition-colors",
        // States
        isDisabled && "opacity-50",
      )}
    >
      <button
        type="button"
        data-testid="pitch-tagging-section-toggle"
        data-section={section}
        aria-expanded={isOpen}
        disabled={isDisabled}
        onClick={onToggle}
        className={cn(
          // Size
          "w-full",
          // Layout
          "flex items-center justify-between gap-3",
          // Spacing
          "px-4 py-4",
          // Text
          "text-left",
          // States
          "focus:outline-none disabled:cursor-default",
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-sm font-medium uppercase tracking-wide text-tagging-muted">
            {label}
          </span>
          {isAutoDetected && (
            <AutoBadge
              data-testid="pitch-tagging-auto-badge"
              data-field={field}
            />
          )}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {isLoading ? (
            <Loader2
              data-testid="pitch-tagging-section-loading-status"
              data-section={section}
              aria-label="Waiting on pitch"
              className="h-5 w-5 animate-spin text-tagging-muted"
            />
          ) : (
            <span
              data-testid="pitch-tagging-section-value"
              data-section={section}
              data-value={rawValue ?? undefined}
              className="text-base font-semibold text-gray-900 dark:text-white"
            >
              {value ?? "-"}
            </span>
          )}
          <Chevron className="h-5 w-5 text-gray-900 dark:text-white" />
        </span>
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </section>
  );
};
